import { Show } from 'react-admin';
import { useState } from 'react';
import { Box, Typography, Paper, Chip, Alert, Grid, Card, CardContent, Button, Avatar, IconButton, CircularProgress } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import type { TrainingProviderRecord } from '@/types/entities';
import { Link } from 'react-router-dom';
import { Business, School, CheckCircle, Warning, VerifiedUser, Close, HourglassEmpty, PlaylistAddCheck, Cancel, Schedule, Category } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { useRecordContext, useNavigate, useUpdate, useNotify, useRefresh, useGetList } from 'react-admin';
import { STATUS_COLORS_PROVIDER } from './constants';
import { TrainingProviderCoursesTable, TrainingProviderMetrics, TrainingProviderQuickActions } from './TrainingProviderSections';

const TrainingProviderShowContent = () => {
  const record = useRecordContext();
  const navigate = useNavigate();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isUpdating, setIsUpdating] = useState(false);
  const [now] = useState(() => Date.now());
  const { data: providerCourses, total: providerCoursesTotal } = useGetList('courses', { filter: { 'trainingProvider.id': record?._id || '' }, pagination: { page: 1, perPage: 100 } });

  if (!record) return null;
  const daysUntilExpiry = record.accreditationExpiry ? Math.ceil((new Date(record.accreditationExpiry).getTime() - now) / (1000 * 60 * 60 * 24)) : null;
  const isExpiring = daysUntilExpiry !== null && daysUntilExpiry <= 60;
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry <= 0;
  const stages = ['Pending', 'Active', 'Suspended', 'Expired'];
  const stageIndex = stages.indexOf(record.accreditationStatus);

  const handleAction = async (data: Record<string, unknown>) => {
    setIsUpdating(true);
    try {
      await update('training-providers', { id: record.id, data });
      notify('Accreditation status updated', { type: 'success' });
      refresh();
    } catch {
      notify('Failed to update accreditation status', { type: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/training-providers')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>
      <PageDescription entity="Training Provider" description="A registered training provider offering accredited courses and qualifications." icon={<School />} />
      {record.accreditationStatus === 'Expired' && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>
          Accreditation expired — provider cannot be used for new training until renewed
        </Alert>
      )}
      {record.accreditationStatus === 'Suspended' && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
          Provider is suspended — contact HR Manager for approval to use this provider
        </Alert>
      )}
      {isExpiring && record.accreditationStatus === 'Active' && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Schedule />}>
          Accreditation expiring in {daysUntilExpiry} days — renewal required
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #e65100, #ef6c00)', color: 'primary.contrastText', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'secondary.main', fontSize: 24 }}>
            <Business sx={{ fontSize: 32 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>{record.providerName}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip label={record.accreditationStatus} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
              <Chip label={record.status} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Provider ID</Typography>
            <Typography variant="h6" fontWeight={700}>{record._id?.split(':')[1]?.substring(0, 8).toUpperCase() || 'N/A'}</Typography>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {stages.map((stage, index) => {
          const isCompleted = index < stageIndex;
          const isCurrent = index === stageIndex;
          const isNext = index === stageIndex + 1;
          return (
            <Card
              key={stage}
              variant="outlined"
              onClick={() => isNext && !isUpdating && handleAction({ accreditationStatus: stage })}
              sx={{
                flex: 1,
                cursor: isNext && !isUpdating ? 'pointer' : 'default',
                bgcolor: isCompleted ? 'success.main' : isCurrent ? 'primary.main' : 'grey.100',
                color: isCompleted || isCurrent ? 'white' : 'text.secondary',
                '&:hover': isNext && !isUpdating ? { boxShadow: 2 } : {},
                opacity: isCompleted || isCurrent || isNext ? 1 : 0.5,
                transition: 'all 0.2s',
                border: isCurrent ? 2 : 1,
                borderColor: isCurrent ? 'primary.dark' : 'divider',
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 1.5, px: 1 }}>
                {isCurrent && <HourglassEmpty sx={{ fontSize: 20, mb: 0.5, display: 'block', mx: 'auto' }} />}
                <Typography variant="body2" fontWeight={isCurrent || isNext ? 600 : 400}>
                  {stage}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {['Pending', 'Active', 'Suspended'].includes(record.accreditationStatus) && (
        <Paper variant="outlined" sx={{ p: 3, my: 3, bgcolor: 'action.hover', borderTop: 4, borderTopColor: 'primary.main', borderLeft: 0, borderRight: 0, borderBottom: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PlaylistAddCheck fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Process Actions</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {record.accreditationStatus === 'Pending' && (
              <Button variant="contained" color="success" startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />} disabled={isUpdating} onClick={() => handleAction({ accreditationStatus: 'Active' })}>
                Approve Accreditation
              </Button>
            )}
            {record.accreditationStatus === 'Active' && (
              <>
                <Button variant="outlined" color="warning" startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <Warning />} disabled={isUpdating} onClick={() => handleAction({ accreditationStatus: 'Suspended' })}>
                  Suspend
                </Button>
                <Button variant="outlined" color="error" startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <Cancel />} disabled={isUpdating} onClick={() => handleAction({ accreditationStatus: 'Expired' })}>
                  Mark Expired
                </Button>
              </>
            )}
            {record.accreditationStatus === 'Suspended' && (
              <Button variant="contained" color="success" startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <VerifiedUser />} disabled={isUpdating} onClick={() => handleAction({ accreditationStatus: 'Active' })}>
                Reinstate
              </Button>
            )}
          </Box>
        </Paper>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <VerifiedUser color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>Accreditation Details</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">SETA Accreditation Number</Typography>
                <Typography variant="body1" fontWeight={500}>{record.setaAccreditationNumber || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">CIPC Registration</Typography>
                <Typography variant="body1">{record.providerRegistrationNumber || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">Accreditation Status</Typography>
                <Chip label={record.accreditationStatus} size="small" color={STATUS_COLORS_PROVIDER[record.accreditationStatus] as ChipColor} sx={{ mt: 0.5 }} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">Accreditation Expiry</Typography>
                <Typography variant="body1" color={isExpired ? 'error.main' : isExpiring ? 'warning.main' : 'inherit'}>
                  {record.accreditationExpiry || '—'} {daysUntilExpiry !== null ? `(${daysUntilExpiry}d)` : ''}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Business color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>Contact Information</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">Contact Person</Typography>
                <Typography variant="body1">{record.legalEntity?.companyName || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body1">{record.legalEntity?.email || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{record.legalEntity?.phone || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">Address</Typography>
                <Typography variant="body1">
                  {record.physicalAddress ? `${record.physicalAddress.streetAddress}, ${record.physicalAddress.city}, ${record.physicalAddress.province}` : '—'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {record.industrySectors?.length > 0 && (
            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Category color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>Industry Sectors</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {record.industrySectors.map((sector: string) => (
                  <Chip key={sector} label={sector} size="small" />
                ))}
              </Box>
            </Paper>
          )}
        </Grid>

        <TrainingProviderCoursesTable providerCourses={providerCourses ?? []} providerCoursesTotal={providerCoursesTotal ?? 0} />
        <TrainingProviderMetrics record={record as unknown as TrainingProviderRecord} />
        <TrainingProviderQuickActions record={record as unknown as TrainingProviderRecord} />
      </Grid>
    </Box>
  );
};

export const TrainingProviderShow = () => (
  <Show>
    <TrainingProviderShowContent />
  </Show>
);
